import { Container, Graphics, Rectangle } from "pixi.js"
import { EmberTutorialPrompt } from "game-common/models"

import { TextRenderer } from "../text/text_renderer"
import { Fader } from "./fader"

// The first-run ember tutorial invitation: a single low bar that says the Lord has something to
// show you, with "Show me" / "Skip" and a countdown that drains from left to right. It is
// deliberately NOT a modal — it sits low on the screen, hugs its content, and leaves the camp
// fully visible, because the Lord is already speaking behind it.
//
// When the countdown runs out the bar resolves itself to ACCEPT (the timeout is a way to get the
// invitation off the screen, not a way to decline it) and reports that with `reason: "timeout"` so
// the server can tell an ignored prompt from a chosen one.
//
// Styled to match the role picker / command wheel (warm amber rim, dark glass), drawn entirely
// here, and driven the same way GuiManager drives those: bind -> fadeIn -> fadeOut.

export type TutorialPromptChoice = "accept" | "skip"
export type TutorialPromptReason = "click" | "timeout"

// Fade timing — mirrors RolePicker/CommandWheel (~100ms each way).
const FADE_INTERVAL_MS = 5
const FADE_STEP = 0.05

// Ember-lit palette, shared with the role picker.
const RIM = 0xf2a94e
const PANEL_FILL = 0x1b1512
const PANEL_ALPHA = 0.92
const TEXT_COLOR = 0xfff3e0
const ACCEPT_COLOR = 0x7be08a
const SKIP_COLOR = 0x9a8e82
const BUTTON_IDLE_FILL = 0x2a201a
const BUTTON_HOVER_FILL = 0x33271e
const BUTTON_IDLE_RIM = 0x6b4f38
const METER_TRACK = 0x3a2c22

// Overall footprint scale. The stage applies a 1.3x vertical scale (client_util SCALER), so the
// bar is authored a little large and scaled down, exactly like RolePicker does.
const WIDGET_SCALE = 0.82

const PAD = 13
const PANEL_W = 420
const PANEL_CORNER = 10
const BUTTON_W = 96
const BUTTON_H = 26
const BUTTON_GAP = 8
const BUTTON_CORNER = 7
const METER_H = 3

// The bar is laid out in ROWS -- title, body, then the buttons on their own line -- and never in
// columns. Buttons beside the text only fits copy short enough to clear them, and the copy is
// written on the SERVER: the moment a line got a few words longer it ran underneath the buttons.
// So the text gets the full inner width to wrap into, and the panel grows to whatever that needs.
const TEXT_W = PANEL_W - PAD * 2
const TITLE_BODY_GAP = 2
// Clear air between the last line of copy and the button row.
const BODY_BUTTONS_GAP = 12
// A floor, so a one-line prompt still reads as a bar rather than a sliver.
const PANEL_H_MIN = 84

// Fallback line heights, used only where a Text measures as zero (a headless/mocked canvas): the
// panel must never collapse onto its own buttons just because nothing could be measured.
const TITLE_LINE_H = 19
const BODY_LINE_H = 15

// Origin (0,0) is the panel's top-left; GuiManager positions it against the stage. The buttons are
// right-aligned; their row sits at the bottom, at a y computed from the measured text.
const ACCEPT_X = PANEL_W - PAD - BUTTON_W * 2 - BUTTON_GAP
const SKIP_X = PANEL_W - PAD - BUTTON_W

interface ButtonHandle {
    container: Container
    bg: Graphics
    color: number
    hovered: boolean
}

export interface TutorialPromptProps {
    onChoose?: (choice: TutorialPromptChoice, reason: TutorialPromptReason) => void
}

export class TutorialPrompt extends Container {
    private onChoose?: (choice: TutorialPromptChoice, reason: TutorialPromptReason) => void
    private bg: Graphics
    private meter: Graphics
    private textLayer: Container
    private buttonLayer: Container
    private accept?: ButtonHandle
    private skip?: ButtonHandle

    // Height the current copy needs (see setPrompt). Everything that touches the bottom edge --
    // the background, the meter, the button row -- reads it rather than a constant.
    private panelH: number = PANEL_H_MIN

    // Countdown state. `remainingMs` only ticks while the bar is open; at 0 it resolves to accept.
    private totalMs: number = 0
    private remainingMs: number = 0

    private opened: boolean = false
    private fadeInFader: Fader | null = null
    private fadeOutFader: Fader | null = null

    constructor(props: TutorialPromptProps = {}) {
        super()
        this.onChoose = props.onChoose
        this.scale.set(WIDGET_SCALE)

        // Swallow pointer events over the bar so a click on it never falls through to a world
        // attack (the buttons handle their own clicks; this covers the text/gaps).
        this.interactive = true
        this.hitArea = new Rectangle(0, 0, PANEL_W, this.panelH)
        this.on("pointerdown", (event: any) => event.stopPropagation())
        this.on("pointermove", (event: any) => event.stopPropagation())
        this.on("rightclick", (event: any) => event.stopPropagation())
        this.on("contextmenu", (event: any) => {
            event.preventDefault()
            event.stopPropagation()
        })

        this.bg = new Graphics()
        this.addChild(this.bg)
        this.meter = new Graphics()
        this.addChild(this.meter)
        this.textLayer = new Container()
        this.addChild(this.textLayer)
        this.buttonLayer = new Container()
        this.addChild(this.buttonLayer)

        this.drawBackground()
        this.visible = false
    }

    // On-stage footprint (authored size x WIDGET_SCALE), so GuiManager can place the bar without
    // reaching into pixi's measured bounds (which include the glow).
    public static stageWidth(): number {
        return PANEL_W * WIDGET_SCALE
    }

    // Instance-side, not static: the bar's height depends on the copy the server sent, so whoever
    // anchors it to the bottom of the screen has to re-read this after setPrompt.
    public stageHeight(): number {
        return this.panelH * WIDGET_SCALE
    }

    // Dark "glass" bar with a warm rim and a faint outer glow (a fatter low-alpha copy rather than
    // a blur filter, which matters on low-end GPUs).
    private drawBackground() {
        const g = this.bg
        g.clear()

        g.beginFill(RIM, 0.06)
        g.drawRoundedRect(-5, -5, PANEL_W + 10, this.panelH + 10, PANEL_CORNER + 5)
        g.endFill()

        g.lineStyle(2, RIM, 0.9)
        g.beginFill(PANEL_FILL, PANEL_ALPHA)
        g.drawRoundedRect(0, 0, PANEL_W, this.panelH, PANEL_CORNER)
        g.endFill()
    }

    // The countdown: a thin track pinned to the bottom inside edge, with an amber fill that drains
    // left to right. Redrawn only when the filled width actually changes by a visible amount.
    private drawMeter() {
        const g = this.meter
        const x = PAD
        const y = this.panelH - PAD / 2 - METER_H
        const w = TEXT_W
        const pct = this.totalMs > 0 ? Math.max(0, Math.min(1, this.remainingMs / this.totalMs)) : 0

        g.clear()
        g.beginFill(METER_TRACK, 0.9)
        g.drawRoundedRect(x, y, w, METER_H, METER_H / 2)
        g.endFill()
        if (pct > 0) {
            g.beginFill(RIM, 0.95)
            g.drawRoundedRect(x, y, Math.max(METER_H, w * pct), METER_H, METER_H / 2)
            g.endFill()
        }
    }

    private makeButton(label: string, x: number, y: number, color: number, choice: TutorialPromptChoice): ButtonHandle {
        const container = new Container()
        container.x = x
        container.y = y
        container.interactive = true
        container.cursor = "pointer"
        container.hitArea = new Rectangle(0, 0, BUTTON_W, BUTTON_H)

        const bg = new Graphics()
        container.addChild(bg)

        const text = new TextRenderer().color(color).fontSize(13).bold().align("center").render(label)
        text.x = BUTTON_W / 2 - text.width / 2
        text.y = BUTTON_H / 2 - text.height / 2
        container.addChild(text)

        const handle: ButtonHandle = { container, bg, color, hovered: false }
        this.drawButton(handle)

        container.on("pointerover", () => {
            handle.hovered = true
            this.drawButton(handle)
        })
        container.on("pointerout", () => {
            handle.hovered = false
            this.drawButton(handle)
        })
        container.on("pointerdown", (event: any) => {
            event.stopPropagation()
            this.choose(choice, "click")
        })

        this.buttonLayer.addChild(container)
        return handle
    }

    private drawButton(handle: ButtonHandle) {
        const g = handle.bg
        g.clear()
        if (handle.hovered) {
            g.beginFill(handle.color, 0.16)
            g.drawRoundedRect(-3, -3, BUTTON_W + 6, BUTTON_H + 6, BUTTON_CORNER + 3)
            g.endFill()
            g.lineStyle(2, handle.color, 1)
            g.beginFill(BUTTON_HOVER_FILL, 0.97)
        } else {
            g.lineStyle(1.5, BUTTON_IDLE_RIM, 0.7)
            g.beginFill(BUTTON_IDLE_FILL, 0.9)
        }
        g.drawRoundedRect(0, 0, BUTTON_W, BUTTON_H, BUTTON_CORNER)
        g.endFill()
    }

    private clearHover() {
        ;[this.accept, this.skip].forEach(button => {
            if (button?.hovered) {
                button.hovered = false
                this.drawButton(button)
            }
        })
    }

    // Answer once and only once: the first choice closes the bar, so a click landing in the same
    // frame the countdown expires can't send two answers.
    private choose(choice: TutorialPromptChoice, reason: TutorialPromptReason) {
        if (!this.opened) {
            return
        }
        this.opened = false
        this.onChoose?.(choice, reason)
    }

    // Rebuild the bar around a server-authored prompt (copy and countdown both come from the
    // server, so the wording can be retuned without a client release).
    public setPrompt(prompt: EmberTutorialPrompt) {
        this.textLayer.removeChildren()
        this.buttonLayer.removeChildren()
        this.accept = undefined
        this.skip = undefined

        // Both lines wrap inside the bar rather than running off it (or under the buttons): the
        // wrap width is set at BUILD time so the measured height below is true on the first frame.
        const title = new TextRenderer()
            .color(TEXT_COLOR)
            .fontSize(15)
            .bold()
            .wordWrap(TEXT_W)
            .render(prompt.title || "")
        title.x = PAD
        title.y = PAD - 2
        this.textLayer.addChild(title)

        const body = new TextRenderer().color(TEXT_COLOR).fontSize(11).wordWrap(TEXT_W).render(prompt.body || "")
        body.x = PAD
        body.y = title.y + Math.max(title.height, TITLE_LINE_H) + TITLE_BODY_GAP
        body.alpha = 0.82
        this.textLayer.addChild(body)

        // Grow the panel around the copy, then hang the button row off the BOTTOM of it. However
        // long the server's wording turns out to be, the buttons end up below it, never over it.
        const textBottom = body.y + Math.max(body.height, BODY_LINE_H)
        this.panelH = Math.max(PANEL_H_MIN, textBottom + BODY_BUTTONS_GAP + BUTTON_H + PAD)
        const buttonsY = this.panelH - PAD - BUTTON_H

        this.accept = this.makeButton(prompt.acceptLabel || "Show me", ACCEPT_X, buttonsY, ACCEPT_COLOR, "accept")
        this.skip = this.makeButton(prompt.skipLabel || "Skip", SKIP_X, buttonsY, SKIP_COLOR, "skip")

        this.hitArea = new Rectangle(0, 0, PANEL_W, this.panelH)
        this.drawBackground()

        this.totalMs = Math.max(0, prompt.timeoutMs || 0)
        this.remainingMs = this.totalMs
        this.drawMeter()
    }

    // Where the button row currently sits, in authored (unscaled) coordinates. Exposed so a test
    // can assert the thing that broke here: that the copy never reaches it.
    public buttonsTop(): number {
        return this.panelH - PAD - BUTTON_H
    }

    // The bottom of the last line of copy, in the same coordinates.
    public textBottom(): number {
        let bottom = 0
        this.textLayer.children.forEach((child: any) => {
            bottom = Math.max(bottom, child.y + Math.max(child.height, BODY_LINE_H))
        })
        return bottom
    }

    // Drain the countdown. Called every frame by GuiManager while the bar is up; deltaTime is in
    // milliseconds (same contract as CountdownTimer.update). A prompt with timeoutMs 0 never
    // expires on its own.
    public update(deltaTime: number) {
        if (!this.opened || this.totalMs <= 0) {
            return
        }
        this.remainingMs = Math.max(0, this.remainingMs - deltaTime)
        this.drawMeter()
        if (this.remainingMs <= 0) {
            this.choose("accept", "timeout")
        }
    }

    public remaining(): number {
        return this.remainingMs
    }

    public isOpen(): boolean {
        return this.opened
    }

    // Reveal by fading in, interrupting an in-progress dismiss (mirrors RolePicker.fadeIn).
    public fadeIn() {
        this.opened = true

        if (this.fadeOutFader) {
            this.fadeOutFader.stop(true)
            this.fadeOutFader = null
        }
        if (this.fadeInFader) {
            return
        }

        if (!this.visible) {
            this.alpha = 0
        }
        this.visible = true

        if (this.alpha >= 1) {
            this.alpha = 1
            return
        }

        this.fadeInFader = new Fader(this, {
            direction: "in",
            minAlpha: 1.0,
            fadeInterval: FADE_INTERVAL_MS,
            fadeAlphaDecrement: FADE_STEP,
            releaseCallback: () => {
                this.fadeInFader = null
                this.alpha = 1
            },
        })
        this.fadeInFader.update()
    }

    // Dismiss by fading out, hiding once transparent (mirrors RolePicker.fadeOut).
    public fadeOut() {
        this.opened = false
        this.clearHover()

        if (this.fadeInFader) {
            this.fadeInFader.stop(true)
            this.fadeInFader = null
        }
        if (this.fadeOutFader || !this.visible) {
            return
        }

        this.fadeOutFader = new Fader(this, {
            direction: "out",
            minAlpha: 0.0,
            fadeInterval: FADE_INTERVAL_MS,
            fadeAlphaDecrement: FADE_STEP,
            releaseCallback: () => {
                this.fadeOutFader = null
                this.visible = false
                this.alpha = 1.0
            },
        })
        this.fadeOutFader.update()
    }
}
